/**
 * SmarterHubStats.jsx — unified per-role profile panel matching the
 * design spec (PDF). Three roles (Reader / Writer / Admin) — each with
 * its own gradient header, quick stats row, progress bars, and tabbed
 * section grid.
 *
 * Data source: GET /api/stats/profile?role={mode}
 *   - admin can pass any role (?role=writer to QA writer view)
 *   - everyone else gets their own role; ?role= ignored server-side
 *
 * Composition:
 *   <SmarterHubStats />                     // auto-detects role
 *   <SmarterHubStats forceRole="writer" />  // admins QA mode
 *
 * Embeds <StatBars> and <SectionPanel> co-located below to keep this
 * one-file feature self-contained.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, BookOpen, PenLine, TrendingUp, TrendingDown, ChevronDown } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { getAdminViewingAs, onSpaceChange } from '../utils/spaceContext';
import Sparkline from './Sparkline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MODE_ICONS = {
  reader: BookOpen,
  writer: PenLine,
  admin: ShieldCheck,
};

// ─────────────────────────────────── StatBars
const StatBars = ({ bars }) => (
  <div className="px-4 sm:px-6 py-4 space-y-3.5" data-testid="stat-bars">
    {bars.map((b, i) => (
      <div key={i} className="flex items-center gap-3">
        <span className="text-[11px] uppercase tracking-widest text-slate-400 font-bold flex-shrink-0 w-44">
          {b.label}
        </span>
        <div className="flex-1 h-2.5 rounded-full bg-slate-800/80 overflow-hidden ring-1 ring-slate-700/40">
          <div
            className="h-full rounded-full transition-all duration-700 shadow-inner"
            style={{
              width: `${b.value}%`,
              background: `linear-gradient(90deg, ${b.color}cc 0%, ${b.color} 100%)`,
              boxShadow: `0 0 12px ${b.color}55`,
            }}
          />
        </div>
        <span
          className="text-sm font-black tabular-nums w-10 text-right"
          style={{ color: b.color }}
        >
          {b.value}
        </span>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────── SectionPanel
const SectionPanel = ({ sections }) => {
  const [active, setActive] = useState(sections[0]?.key);
  const current = sections.find((s) => s.key === active) || sections[0];
  if (!current) return null;
  return (
    <div className="border-t border-slate-700/60" data-testid="section-panel">
      {/* Tab nav */}
      <div className="flex gap-1 px-3 sm:px-5 pt-3 overflow-x-auto no-scrollbar">
        {sections.map((s) => {
          const isActive = s.key === active;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setActive(s.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                isActive
                  ? 'shadow-md'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              style={isActive ? { backgroundColor: s.color, color: '#0f172a' } : undefined}
              data-testid={`section-tab-${s.key}`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Data grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-4 sm:px-6 py-4">
        {current.data.map((it, idx) => {
          const isLegendary = it.type === 'legendary';
          return (
            <div
              key={idx}
              className={`relative flex items-start gap-3 p-3 rounded-xl border overflow-hidden ${
                isLegendary
                  ? 'bg-gradient-to-br from-amber-500/20 via-amber-600/10 to-amber-700/5 border-amber-400/50 shadow-lg shadow-amber-900/30'
                  : 'bg-slate-800/40 border-slate-700/60 hover:border-slate-600 transition-colors'
              }`}
              data-testid={`section-data-${current.key}-${idx}`}
            >
              {/* Subtle shimmer band on legendary cards — gives the
                  "earned tier" cards a perpetually-alive, premium feel
                  without distracting from the value text. */}
              {isLegendary && (
                <span
                  className="pointer-events-none absolute inset-y-0 -left-1 w-1/3 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[shimmer_3s_ease-in-out_infinite]"
                  aria-hidden
                />
              )}
              <div className="flex-1 min-w-0 relative">
                <span className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">
                  {it.label}
                </span>
                <span className={`text-sm font-bold truncate block ${isLegendary ? 'text-amber-100' : 'text-white'}`}>
                  {typeof it.value === 'number'
                    ? Number(it.value).toLocaleString()
                    : it.value}
                </span>
              </div>
              {it.icon && (
                <span
                  className={`text-2xl flex-shrink-0 ${
                    isLegendary ? 'drop-shadow-[0_0_6px_rgba(251,191,36,0.6)]' : 'opacity-80'
                  }`}
                  aria-hidden
                >
                  {it.icon}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────── XPTrendStrip
// 30-day XP sparkline + week-over-week delta. Pulls /api/xp/history once
// on mount; renders nothing if the user has no XP events yet. Used as
// the "trend at a glance" row above StatBars on Reader/Scribe profiles.
const XPTrendStrip = ({ accentColor = '#22d3ee' }) => {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/xp/history?days=30`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (alive) setHistory(d);
      } catch {
        if (alive) setHistory({ history: [] });
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!history) return null;
  const series = (history.history || []).map((d) => d.xp || 0);
  const totalXP = series.reduce((a, b) => a + b, 0);
  if (series.length < 2 || totalXP === 0) return null;

  const last7 = series.slice(-7).reduce((a, b) => a + b, 0);
  const prior7 = series.slice(-14, -7).reduce((a, b) => a + b, 0);
  const delta = prior7 ? Math.round(((last7 - prior7) / prior7) * 100) : 0;
  const trendUp = delta >= 0;

  return (
    <div
      className="border-t border-slate-700/60 px-4 sm:px-6 py-3 flex items-center justify-between gap-3"
      data-testid="xp-trend-strip"
    >
      <div className="flex flex-col min-w-0">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          30-day XP trend
        </span>
        <span className="text-sm font-bold text-white tabular-nums">
          {totalXP.toLocaleString()} XP
        </span>
      </div>
      <Sparkline
        values={series}
        width={140}
        height={32}
        stroke={accentColor}
        fill={`${accentColor}33`}
      />
      <div
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-black tabular-nums shrink-0 ${
          trendUp
            ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
            : 'bg-rose-500/15 text-rose-300 border border-rose-500/40'
        }`}
        data-testid="xp-trend-delta"
      >
        {trendUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {trendUp ? '+' : ''}{delta}%
      </div>
    </div>
  );
};

// ─────────────────────────────────── SmarterHubStats (root)
const SmarterHubStats = ({ forceRole, allowToggle = true, isAdmin = false }) => {
  // "More details" pill — collapsed by default so Profile pages are short
  // and scannable per the Feb 26 2026 UX pass. Tapping the pill reveals
  // the XP trend strip, the publication bars, and the tabbed section grid.
  // Preference persisted so the next visit remembers the user's choice.
  const [detailsOpen, setDetailsOpen] = useState(() => {
    try {
      return localStorage.getItem('sl_hub_stats_details_open') === '1';
    } catch { return false; }
  });
  const toggleDetails = () => {
    setDetailsOpen((v) => {
      const next = !v;
      try { localStorage.setItem('sl_hub_stats_details_open', next ? '1' : '0'); } catch {}
      return next;
    });
  };
  // Admins default to the Admin view (Users/Pending/Sales/Payouts) so they
  // see operational stats first; the toggle still lets them flip to
  // Reader/Writer for QA. Non-admins always start on 'reader' (the server
  // overrides this to their actual role anyway).
  const [mode, setMode] = useState(forceRole || (isAdmin ? 'admin' : 'reader'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);

  const canToggle = allowToggle && isAdmin && !forceRole;

  // Sync mode when isAdmin becomes true asynchronously (e.g. StatsPage
  // resolves checkAuth after first render). Without this, admin sessions
  // land on 'reader' and have to manually flip the toggle. Skip if the
  // user has already chosen a non-default mode.
  useEffect(() => {
    if (forceRole) return;
    if (isAdmin && mode === 'reader') setMode('admin');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, forceRole]);

  // Mirror the global "viewing as" context so flipping the role toggle in
  // the TopBar's Profile context strip swaps this panel's header in-place
  // (Admin Profile ↔ Reader Profile ↔ Scribe Profile) without navigating.
  // The localStorage key uses 'reader' / 'writer' / 'admin' which maps 1:1
  // to our `mode` so we just normalise the value.
  useEffect(() => {
    if (forceRole || !isAdmin) return;
    const apply = () => {
      const v = getAdminViewingAs();
      const next = v === 'reader' ? 'reader' : v === 'writer' ? 'writer' : 'admin';
      setMode((prev) => (prev === next ? prev : next));
    };
    apply();
    return onSpaceChange((_n, axis) => {
      if (axis === 'admin') apply();
    });
  }, [isAdmin, forceRole]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const url = forceRole
          ? `${BACKEND_URL}/api/stats/profile?role=${encodeURIComponent(forceRole)}`
          : isAdmin
            ? `${BACKEND_URL}/api/stats/profile?role=${encodeURIComponent(mode)}`
            : `${BACKEND_URL}/api/stats/profile`;
        const res = await authFetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await res.json();
        if (alive) {
          setData(d);
          if (!forceRole && d.role && d.role !== mode) setMode(d.role);
        }
      } catch (e) {
        if (alive) setError(typeof e?.message === 'string' ? e.message : 'Failed to load stats');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [mode, forceRole, isAdmin, refreshToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="hub-stats-loading">
        <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-2xl border border-rose-500/40 bg-rose-900/20 p-6 text-rose-200 text-sm" data-testid="hub-stats-error" role="alert">
        <p>Couldn’t load server-calculated stats — {error || 'unknown error'}.</p>
        <button
          type="button"
          onClick={() => setRefreshToken((token) => token + 1)}
          className="mt-3 rounded-lg border border-rose-300/40 px-3 py-1.5 text-xs font-bold text-rose-100 transition-colors hover:bg-rose-500/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-200"
          data-testid="hub-stats-retry"
        >
          Retry
        </button>
      </div>
    );
  }

  const roleLabels = {
    reader: { eyebrow: 'Reader stats', color: 'text-cyan-300', ring: 'border-cyan-400/25', bg: 'bg-cyan-400/10' },
    writer: { eyebrow: 'Scribe stats', color: 'text-purple-300', ring: 'border-purple-400/25', bg: 'bg-purple-400/10' },
    admin: { eyebrow: 'Platform stats', color: 'text-amber-300', ring: 'border-amber-400/25', bg: 'bg-amber-400/10' },
  };
  const RL = roleLabels[data.role] || roleLabels.reader;
  const HeaderIcon = MODE_ICONS[data.role] || BookOpen;

  return (
    <div
      className="max-w-3xl mx-auto rounded-2xl bg-[#12131A] border border-white/10 overflow-hidden"
      data-testid="smarter-hub-stats"
    >
      {/* Header — sharp, dark, matches StreakHeroCard + rest of app.
          Feb 14 2026 user asked to kill the old glossy gradient banner
          that said "READER PROFILE" / "SCRIBE PROFILE" in a purple→cyan
          shine. Replaced with the same eyebrow + display-heading
          pattern used everywhere else on the app. */}
      <div className="px-5 sm:px-6 py-4 sm:py-5 flex items-center gap-3.5 border-b border-white/5">
        <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-2xl border ${RL.ring} ${RL.bg} grid place-items-center flex-shrink-0`}>
          <HeaderIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${RL.color}`} strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`eyebrow ${RL.color}`} data-testid="hub-stats-eyebrow">{RL.eyebrow}</p>
          <h2 className="font-display text-lg sm:text-xl font-black text-white tracking-tight leading-tight truncate" data-testid="hub-stats-title">
            {data.label}
          </h2>
          <p className="text-[11px] text-white/50 mt-0.5 truncate">{data.tagline}</p>
        </div>
      </div>

      {/* Quick stats row — sharp, dark, hairline dividers only. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-white/5 border-b border-white/5">
        {data.quickStats.map((s, i) => (
          <div
            key={i}
            className="px-3 py-4 text-center"
            data-testid={`hub-quickstat-${i}`}
          >
            <div
              className="text-2xl sm:text-3xl font-black tabular-nums leading-none"
              style={{ color: s.color }}
            >
              {typeof s.value === 'number' ? Number(s.value).toLocaleString() : s.value}
            </div>
            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-white/45 font-bold mt-1.5">
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* "More details" pill — toggles the heavier sections so the
          Profile page stays short and scannable on small screens. */}
      <div className="px-4 sm:px-6 py-3">
        <button
          type="button"
          onClick={toggleDetails}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-black text-white uppercase tracking-widest transition-colors"
          aria-expanded={detailsOpen}
          aria-controls="hub-stats-details"
          data-testid="hub-stats-details-toggle"
        >
          {detailsOpen ? 'Hide details' : 'Show more details'}
          <ChevronDown className={`w-4 h-4 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <div
        id="hub-stats-details"
        className={`grid transition-all duration-300 ease-in-out ${
          detailsOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
        data-testid="hub-stats-details-panel"
      >
        <div className="overflow-hidden">
          {/* 30-day XP trend — hidden for admin role (uses different data) */}
          {(data.role === 'reader' || data.role === 'writer') && (
            <XPTrendStrip accentColor={data.accentA || '#22d3ee'} />
          )}

          {/* Bars + sections */}
          <StatBars bars={data.bars} />
          <SectionPanel sections={data.sections} />
        </div>
      </div>

      {/* Mode toggle (admin only) */}
      {canToggle && (
        <div className="border-t border-white/5 p-3 sm:p-4 flex justify-center">
          <div className="inline-flex p-0.5 rounded-full bg-white/5 border border-white/10">
            {['reader', 'writer', 'admin'].map((r) => {
              const Icon = MODE_ICONS[r];
              const isActive = mode === r;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setMode(r)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-widest transition-all ${
                    isActive
                      ? 'bg-white text-black'
                      : 'text-white/50 hover:text-white'
                  }`}
                  data-testid={`hub-stats-mode-${r}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {r}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmarterHubStats;
